import React from 'react';
import type { ChartDashboardPayload } from '@cotera/api';
import { dashboardChartToArtifactPayload } from '@cotera/api';
import { cn } from '@cotera/client/v0/lib/utils';
import { getChartColorForLabel } from './chart-config';
import { chartPayloadAccentLabel } from './chart-label-colors';
import { SPARK_VIEW_HEIGHT, SPARK_VIEW_WIDTH } from './chart-spark-utils';
import { SparkShapes } from './chart-spark-shapes';

const DASHBOARD_SPARK_HEIGHT = 44;
const FACET_GAP = 1.2;

/**
 * One unified spark thumbnail for a dashboard — each chart gets a colored facet
 * in a single SVG instead of separate duplicate-looking previews.
 */
export const ChartDashboardSparkPreview: React.FC<{
  payload: ChartDashboardPayload;
  className?: string;
  height?: number;
}> = ({ payload, className, height = DASHBOARD_SPARK_HEIGHT }) => {
  const charts = payload.charts;
  if (charts.length === 0) {
    return null;
  }

  if (charts.length === 1) {
    const only = charts[0];
    if (only === undefined) {
      return null;
    }
    return (
      <div
        className={cn('w-full min-w-[48px] overflow-hidden rounded', className)}
        style={{ height }}
      >
        <svg
          viewBox={`0 0 ${SPARK_VIEW_WIDTH} ${SPARK_VIEW_HEIGHT}`}
          width="100%"
          height={height}
          preserveAspectRatio="none"
          className="block"
          aria-hidden
        >
          <SparkShapes payload={dashboardChartToArtifactPayload(only)} />
        </svg>
      </div>
    );
  }

  const facetWidth = SPARK_VIEW_WIDTH / charts.length;

  return (
    <div
      className={cn('w-full min-w-[48px] overflow-hidden rounded', className)}
      style={{ height }}
    >
      <svg
        viewBox={`0 0 ${SPARK_VIEW_WIDTH} ${SPARK_VIEW_HEIGHT}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        className="block"
        aria-hidden
      >
        {charts.map((chart, index) => {
          const innerWidth = facetWidth - FACET_GAP;
          const scaleX = innerWidth / SPARK_VIEW_WIDTH;
          const x = index * facetWidth + FACET_GAP / 2;
          return (
            <g
              key={chart.chart_id}
              transform={`translate(${x}, 0) scale(${scaleX}, 1)`}
            >
              <SparkShapes payload={dashboardChartToArtifactPayload(chart)} />
            </g>
          );
        })}
        {charts.slice(1).map((_chart, index) => {
          const dividerX = (index + 1) * facetWidth;
          return (
            <line
              key={`divider-${index}`}
              x1={dividerX}
              y1={2}
              x2={dividerX}
              y2={SPARK_VIEW_HEIGHT - 2}
              className="stroke-border"
              strokeWidth={0.5}
              strokeOpacity={0.8}
            />
          );
        })}
      </svg>
      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
        {charts.map((chart) => {
          const accentLabel = chartPayloadAccentLabel(
            dashboardChartToArtifactPayload(chart)
          );
          return (
            <span
              key={chart.chart_id}
              className="inline-flex max-w-full items-center gap-1 text-[10px] text-muted-foreground"
            >
              <span
                className="size-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: getChartColorForLabel(accentLabel) }}
              />
              <span className="truncate">{chart.title}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};
